import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { useAuth, useClerk } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  AlignLeft,
  BarChart2,
  Book,
  Bookmark,
  ChevronDown,
  Clipboard,
  Clock,
  DollarSign,
  FileText,
  Filter,
  Flag,
  Folder,
  Globe,
  Image,
  Link2,
  LogOut,
  Mail,
  Menu,
  MessageCircle,
  PanelLeft,
  PieChart,
  Search,
  Send,
  Share2,
  Smartphone,
  Target,
  TrendingUp,
  Users,
  Video,
  Wrench,
  X,
} from 'lucide-react';
import { AdminUnreadDot } from '@/components/admin/AdminUnreadDot';
import { AdminPortalContext } from '@/components/admin/ui/portal';
import { apiJson } from '@/lib/api';
import { COACH_OUTREACH_REPLIES_QUERY_KEY } from '@/lib/coachOutreachRepliesStorage';
import { useCoachOutreachUnreadReplies } from '@/hooks/useCoachOutreachUnreadReplies';
import { LAWYER_OUTREACH_REPLIES_QUERY_KEY } from '@/lib/lawyerOutreachRepliesStorage';
import { useLawyerOutreachUnreadReplies } from '@/hooks/useLawyerOutreachUnreadReplies';
import { COUNTY_COURT_OUTREACH_REPLIES_QUERY_KEY } from '@/lib/countyCourtOutreachRepliesStorage';
import { useCountyCourtOutreachUnreadReplies } from '@/hooks/useCountyCourtOutreachUnreadReplies';
import { PRESS_OUTREACH_REPLIES_QUERY_KEY } from '@/lib/pressOutreachRepliesStorage';
import { usePressOutreachUnreadReplies } from '@/hooks/usePressOutreachUnreadReplies';
import type {
  ProfessionalDirectoryProfileListResponse,
  UserResponse,
} from '@/lib/types';
import faviconPng from '@/assets/favicon.png';

type OutreachRepliesListResponse = {
  replies: Array<{ received_at: string | null }>;
};

const ADMIN_DASHBOARD_HREF = import.meta.env.DEV
  ? 'http://localhost:5173/admin/'
  : 'https://harborcoparent.com/admin/';

let adminNovaStylesPromise: Promise<unknown> | null = null;

function loadAdminNovaStyles() {
  adminNovaStylesPromise ??= Promise.all([
    import('@/styles/admin-nova.css'),
    import('@fontsource-variable/geist'),
  ]);
  return adminNovaStylesPromise;
}

type AdminNavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  active: boolean;
  search?: Record<string, unknown>;
  showPendingBadge?: boolean;
  showUnreadBadge?: boolean;
  unreadBadgeLabel?: string;
};

type AdminNavGroup = {
  id: string;
  label: string;
  items: AdminNavItem[];
};

function AdminLayout() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [manualGroupId, setManualGroupId] = useState<string | null | undefined>(
    undefined
  );
  const [portalEl, setPortalEl] = useState<HTMLDivElement | null>(null);
  const [stylesReady, setStylesReady] = useState(false);

  useEffect(() => {
    let live = true;
    void loadAdminNovaStyles().then(() => {
      if (live) setStylesReady(true);
    });
    return () => {
      live = false;
    };
  }, []);

  const userQuery = useQuery({
    queryKey: ['admin', 'user'],
    queryFn: () => apiJson<UserResponse>(getToken, '/v1/user'),
    enabled: isLoaded && !!isSignedIn,
    retry: 1,
  });

  const pendingDirectoryProfilesQuery = useQuery({
    queryKey: ['admin-directory-profiles', 'pending'],
    queryFn: () =>
      apiJson<ProfessionalDirectoryProfileListResponse>(
        getToken,
        '/v1/admin/directory-profiles?status=pending'
      ),
    enabled: isLoaded && !!isSignedIn,
    retry: 1,
  });

  const coachOutreachRepliesQuery = useQuery({
    queryKey: COACH_OUTREACH_REPLIES_QUERY_KEY,
    queryFn: () =>
      apiJson<OutreachRepliesListResponse>(
        getToken,
        '/v1/admin/coach-outreach/replies?limit=50'
      ),
    enabled: isLoaded && !!isSignedIn,
    retry: 1,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const { hasUnread: hasUnreadCoachOutreachReplies } =
    useCoachOutreachUnreadReplies(coachOutreachRepliesQuery.data?.replies);

  const lawyerOutreachRepliesQuery = useQuery({
    queryKey: LAWYER_OUTREACH_REPLIES_QUERY_KEY,
    queryFn: () =>
      apiJson<OutreachRepliesListResponse>(
        getToken,
        '/v1/admin/lawyer-outreach/replies?limit=50'
      ),
    enabled: isLoaded && !!isSignedIn,
    retry: 1,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const { hasUnread: hasUnreadLawyerOutreachReplies } =
    useLawyerOutreachUnreadReplies(lawyerOutreachRepliesQuery.data?.replies);

  const countyCourtOutreachRepliesQuery = useQuery({
    queryKey: COUNTY_COURT_OUTREACH_REPLIES_QUERY_KEY,
    queryFn: () =>
      apiJson<OutreachRepliesListResponse>(
        getToken,
        '/v1/admin/county-court-outreach/replies?limit=50'
      ),
    enabled: isLoaded && !!isSignedIn,
    retry: 1,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const { hasUnread: hasUnreadCountyCourtOutreachReplies } =
    useCountyCourtOutreachUnreadReplies(
      countyCourtOutreachRepliesQuery.data?.replies
    );

  const pressOutreachRepliesQuery = useQuery({
    queryKey: PRESS_OUTREACH_REPLIES_QUERY_KEY,
    queryFn: () =>
      apiJson<OutreachRepliesListResponse>(
        getToken,
        '/v1/admin/press-outreach/replies?limit=50'
      ),
    enabled: isLoaded && !!isSignedIn,
    retry: 1,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const { hasUnread: hasUnreadPressOutreachReplies } =
    usePressOutreachUnreadReplies(pressOutreachRepliesQuery.data?.replies);

  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      navigate({ to: '/auth/sign-in' });
    }
  }, [isLoaded, isSignedIn, navigate]);

  useEffect(() => {
    if (!userQuery.isFetched || !userQuery.data) return;
    if (!(userQuery.data.roles ?? []).includes('staff')) {
      navigate({ to: '/', search: { hero: undefined } });
    }
  }, [userQuery.isFetched, userQuery.data, navigate]);

  useEffect(() => {
    setMobileMenuOpen(false);
    setManualGroupId(undefined);
  }, [pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const html = document.documentElement;
    const { overflow: htmlOverflow } = html.style;
    const { overflow: bodyOverflow } = document.body.style;
    html.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileMenuOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      html.style.overflow = htmlOverflow;
      document.body.style.overflow = bodyOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [mobileMenuOpen]);

  if (!stylesReady || !isLoaded || userQuery.isLoading) {
    return (
      <div className='admin-portal flex min-h-screen items-center justify-center bg-nova-background'>
        <div className='h-8 w-8 animate-spin rounded-full border-2 border-nova-primary border-t-transparent' />
      </div>
    );
  }

  if (!isSignedIn || !userQuery.data) return null;
  if (!(userQuery.data.roles ?? []).includes('staff')) return null;

  const usersActive =
    pathname === '/admin' ||
    pathname === '/admin/' ||
    pathname.startsWith('/admin/users');
  const pulseActive = pathname.startsWith('/admin/pulse');
  const narrativesActive = pathname.startsWith('/admin/narratives');
  const metricsActive = pathname.startsWith('/admin/metrics');
  const ltvActive = pathname.startsWith('/admin/ltv');
  const subscribersActive = pathname.startsWith('/admin/subscribers');
  const allTimeActive = pathname.startsWith('/admin/all-time');
  const performanceActive = pathname.startsWith('/admin/performance');
  const coachOutreachActive = pathname.startsWith('/admin/coach-outreach');
  const lawyerOutreachActive = pathname.startsWith('/admin/lawyer-outreach');
  const countyCourtOutreachActive = pathname.startsWith(
    '/admin/county-court-outreach'
  );
  const pressOutreachActive = pathname.startsWith('/admin/press-outreach');
  const professionalsActive = pathname.startsWith('/admin/professionals');
  const appVersionActive = pathname.startsWith('/admin/app-version');
  const featureFlagsActive = pathname.startsWith('/admin/feature-flags');
  const divorceFormsActive = pathname.startsWith('/admin/divorce-forms');
  const toolsActive = pathname.startsWith('/admin/tools');
  const journalActive = pathname.startsWith('/admin/journal');
  const chatsActive = pathname.startsWith('/admin/chats');
  const messageAttachmentsActive = pathname.startsWith(
    '/admin/message-attachments'
  );
  const vaultActive = pathname.startsWith('/admin/vault');
  const bookmarksActive = pathname.startsWith('/admin/bookmarks');
  const adsActive = pathname.startsWith('/admin/ads');
  const metaActive = pathname.startsWith('/admin/meta');
  const attributionActive = pathname.startsWith('/admin/attribution');
  const tiktokActive = pathname.startsWith('/admin/tiktok');
  const seoActive = pathname.startsWith('/admin/seo');
  const marketingDomainsActive = pathname.startsWith(
    '/admin/marketing-domains'
  );
  const rateQueueActive = pathname.startsWith('/admin/rate-queue');
  const mainDomainsActive = pathname.startsWith('/admin/main-domains');
  const acquisitionActive = pathname.startsWith('/admin/acquisition');
  const yearlyTrialActive = pathname.startsWith('/admin/yearly-trial');
  const hasPendingDirectoryProfiles =
    (pendingDirectoryProfilesQuery.data?.profiles ?? []).length > 0;
  const handleSignOut = async () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      await signOut();
      navigate({ to: '/auth/sign-in' });
    }
  };

  const navGroups: AdminNavGroup[] = [
    {
      id: 'users-ops',
      label: 'Users & Ops',
      items: [
        { to: '/admin', label: 'Users', icon: Users, active: usersActive },
        {
          to: '/admin/tools',
          label: 'Tools',
          icon: Wrench,
          active: toolsActive,
        },
        {
          to: '/admin/app-version',
          label: 'App Version',
          icon: Smartphone,
          active: appVersionActive,
        },
        {
          to: '/admin/feature-flags',
          label: 'Feature Flags',
          icon: Flag,
          active: featureFlagsActive,
        },
        {
          to: '/admin/performance',
          label: 'Performance',
          icon: Clock,
          active: performanceActive,
          search: { range: '24h', method: undefined, template: undefined },
        },
      ],
    },
    {
      id: 'product',
      label: 'Product',
      items: [
        {
          to: '/admin/pulse',
          label: 'Pulse',
          icon: Activity,
          active: pulseActive,
          search: { date: undefined },
        },
        {
          to: '/admin/narratives',
          label: 'Narratives',
          icon: AlignLeft,
          active: narrativesActive,
          search: { date: undefined },
        },
        {
          to: '/admin/metrics',
          label: 'Metrics',
          icon: TrendingUp,
          active: metricsActive,
          search: { range: '30d' },
        },
        {
          to: '/admin/journal',
          label: 'Journal',
          icon: Book,
          active: journalActive,
        },
        {
          to: '/admin/chats',
          label: 'Chats',
          icon: MessageCircle,
          active: chatsActive,
        },
        {
          to: '/admin/bookmarks',
          label: 'Bookmarks',
          icon: Bookmark,
          active: bookmarksActive,
          search: { range: '30d' },
        },
        {
          to: '/admin/message-attachments',
          label: 'Attachments',
          icon: Image,
          active: messageAttachmentsActive,
        },
        {
          to: '/admin/vault',
          label: 'Vault',
          icon: Folder,
          active: vaultActive,
        },
        {
          to: '/admin/divorce-forms',
          label: 'Divorce Forms',
          icon: FileText,
          active: divorceFormsActive,
        },
      ],
    },
    {
      id: 'growth',
      label: 'Growth',
      items: [
        {
          to: '/admin/acquisition',
          label: 'Acquisition',
          icon: Filter,
          active: acquisitionActive,
        },
        {
          to: '/admin/yearly-trial',
          label: '7-Day Trial',
          icon: Mail,
          active: yearlyTrialActive,
        },
        {
          to: '/admin/tiktok',
          label: 'TikTok',
          icon: Video,
          active: tiktokActive,
          search: { days: '30' },
        },
        {
          to: '/admin/ads',
          label: 'Ads',
          icon: BarChart2,
          active: adsActive,
          search: { days: '30' },
        },
        {
          to: '/admin/meta',
          label: 'Meta',
          icon: Share2,
          active: metaActive,
          search: { days: '30' },
        },
        {
          to: '/admin/attribution',
          label: 'Attribution',
          icon: Target,
          active: attributionActive,
          search: { days: '30' },
        },
        {
          to: '/admin/seo',
          label: 'SEO',
          icon: Search,
          active: seoActive,
          search: { days: '30', tab: 'overview' },
        },
        {
          to: '/admin/marketing-domains',
          label: 'Marketing Domains',
          icon: Link2,
          active: marketingDomainsActive,
          search: { days: '30', host: undefined },
        },
        {
          to: '/admin/rate-queue',
          label: 'Rate Experiment',
          icon: Clipboard,
          active: rateQueueActive,
        },
        {
          to: '/admin/main-domains',
          label: 'Main Domains',
          icon: Globe,
          active: mainDomainsActive,
          search: { days: '30', host: undefined },
        },
      ],
    },
    {
      id: 'revenue',
      label: 'Revenue',
      items: [
        {
          to: '/admin/ltv',
          label: 'LTV',
          icon: DollarSign,
          active: ltvActive,
          search: { range: '90d', lifetime: 'unbounded' },
        },
        {
          to: '/admin/subscribers',
          label: 'Subscribers',
          icon: PieChart,
          active: subscribersActive,
        },
        {
          to: '/admin/all-time',
          label: 'All Time',
          icon: Globe,
          active: allTimeActive,
        },
      ],
    },
    {
      id: 'professionals',
      label: 'Professionals',
      items: [
        {
          to: '/admin/professionals',
          label: 'Professionals',
          icon: Clipboard,
          active: professionalsActive,
          showPendingBadge: hasPendingDirectoryProfiles,
        },
        {
          to: '/admin/coach-outreach',
          label: 'Coach Outreach',
          icon: Send,
          active: coachOutreachActive,
          showUnreadBadge: hasUnreadCoachOutreachReplies,
          unreadBadgeLabel: 'New coach replies since you last opened Replies',
        },
        {
          to: '/admin/lawyer-outreach',
          label: 'Lawyer Outreach',
          icon: Send,
          active: lawyerOutreachActive,
          showUnreadBadge: hasUnreadLawyerOutreachReplies,
          unreadBadgeLabel: 'New lawyer replies since you last opened Replies',
        },
        {
          to: '/admin/county-court-outreach',
          label: 'County Court Outreach',
          icon: Send,
          active: countyCourtOutreachActive,
          showUnreadBadge: hasUnreadCountyCourtOutreachReplies,
          unreadBadgeLabel: 'New clerk replies since you last opened Replies',
        },
        {
          to: '/admin/press-outreach',
          label: 'Press Outreach',
          icon: Send,
          active: pressOutreachActive,
          showUnreadBadge: hasUnreadPressOutreachReplies,
          unreadBadgeLabel: 'New press replies since you last opened Replies',
        },
      ],
    },
  ];

  const activeGroupId =
    navGroups.find((group) => group.items.some((item) => item.active))?.id ??
    null;
  const shownGroupId =
    manualGroupId === undefined ? activeGroupId : manualGroupId;

  const renderNavLink = (item: AdminNavItem, onNavigate?: () => void) => {
    const Icon = item.icon;
    return (
      <Link
        key={item.to}
        to={item.to}
        search={item.search}
        title={item.label}
        onClick={onNavigate}
        className={
          item.active
            ? 'flex h-8 w-full items-center gap-2 rounded-md bg-nova-sidebar-accent px-2 text-sm font-medium text-nova-sidebar-accent-foreground'
            : 'flex h-8 w-full items-center gap-2 rounded-md px-2 text-sm text-nova-sidebar-foreground hover:bg-nova-sidebar-accent hover:text-nova-sidebar-accent-foreground'
        }
      >
        <Icon className='h-4 w-4 shrink-0' aria-hidden />
        <span className='truncate'>{item.label}</span>
        {item.showPendingBadge ? (
          <AdminUnreadDot label='Pending listings to review' />
        ) : null}
        {item.showUnreadBadge ? (
          <AdminUnreadDot
            label={item.unreadBadgeLabel ?? 'New items to review'}
          />
        ) : null}
      </Link>
    );
  };

  const groupHasBadge = (group: AdminNavGroup) =>
    group.items.some((item) => item.showPendingBadge || item.showUnreadBadge);

  const toggleGroup = (groupId: string) => {
    setManualGroupId((current) => {
      const shown = current === undefined ? activeGroupId : current;
      return shown === groupId ? null : groupId;
    });
  };

  const renderGroup = (group: AdminNavGroup, collapsed: boolean) => {
    const GroupIcon = group.items[0]?.icon ?? Users;
    const isOpen = shownGroupId === group.id;
    if (collapsed) {
      return (
        <button
          key={group.id}
          type='button'
          title={group.label}
          onClick={() => {
            setSidebarCollapsed(false);
            setManualGroupId(group.id);
          }}
          className={
            isOpen || group.items.some((item) => item.active)
              ? 'relative flex h-8 w-8 items-center justify-center rounded-md bg-nova-sidebar-accent text-nova-sidebar-accent-foreground'
              : 'relative flex h-8 w-8 items-center justify-center rounded-md text-nova-muted-foreground hover:bg-nova-sidebar-accent hover:text-nova-sidebar-accent-foreground'
          }
        >
          <GroupIcon className='h-4 w-4' aria-hidden />
          {groupHasBadge(group) ? (
            <span className='absolute right-0.5 top-0.5'>
              <AdminUnreadDot label={`Updates in ${group.label}`} />
            </span>
          ) : null}
        </button>
      );
    }
    return (
      <div key={group.id} className='flex flex-col gap-0.5'>
        <button
          type='button'
          onClick={() => toggleGroup(group.id)}
          aria-expanded={isOpen}
          className='flex h-8 w-full items-center gap-2 rounded-md px-2 text-xs font-medium uppercase tracking-wide text-nova-muted-foreground hover:bg-nova-sidebar-accent hover:text-nova-sidebar-accent-foreground'
        >
          <span className='truncate'>{group.label}</span>
          {groupHasBadge(group) ? (
            <AdminUnreadDot label={`Updates in ${group.label}`} />
          ) : null}
          <ChevronDown
            className={`ml-auto h-3.5 w-3.5 shrink-0 transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`}
            aria-hidden
          />
        </button>
        {isOpen
          ? group.items.map((item) => renderNavLink(item, () => undefined))
          : null}
      </div>
    );
  };

  const brand = (compact: boolean) => (
    <a
      href={ADMIN_DASHBOARD_HREF}
      className='flex min-w-0 items-center gap-2 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-nova-ring'
    >
      <img
        src={faviconPng}
        alt=''
        width={28}
        height={28}
        className='h-7 w-7 shrink-0 rounded-md border border-nova-sidebar-border bg-nova-card object-cover'
      />
      {compact ? null : (
        <span className='truncate text-sm font-semibold tracking-tight text-nova-foreground'>
          Harbor Admin
        </span>
      )}
    </a>
  );

  const signOutButton = (compact: boolean) => (
    <button
      type='button'
      onClick={handleSignOut}
      title='Sign out'
      className={`flex h-8 items-center gap-2 rounded-md text-sm text-nova-muted-foreground hover:bg-nova-sidebar-accent hover:text-nova-sidebar-accent-foreground ${
        compact ? 'w-8 justify-center px-0' : 'w-full px-2'
      }`}
    >
      <LogOut className='h-4 w-4 shrink-0' aria-hidden />
      {compact ? null : <span>Sign out</span>}
    </button>
  );

  return (
    <AdminPortalContext.Provider value={portalEl}>
      <div
        ref={setPortalEl}
        className='admin-portal flex min-h-screen bg-nova-background text-nova-foreground'
      >
        <aside
          className={`sticky top-0 hidden h-screen shrink-0 flex-col border-r border-nova-sidebar-border bg-nova-sidebar lg:flex ${
            sidebarCollapsed ? 'w-14' : 'w-60'
          }`}
        >
          <div
            className={`flex h-12 shrink-0 items-center border-b border-nova-sidebar-border ${
              sidebarCollapsed ? 'justify-center px-1' : 'gap-2 px-3'
            }`}
          >
            {brand(sidebarCollapsed)}
          </div>
          <nav
            className={`flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-2 ${
              sidebarCollapsed ? 'items-center' : ''
            }`}
          >
            {navGroups.map((group) => renderGroup(group, sidebarCollapsed))}
          </nav>
          <div className='flex shrink-0 flex-col gap-1 border-t border-nova-sidebar-border p-2'>
            <button
              type='button'
              onClick={() => setSidebarCollapsed((collapsed) => !collapsed)}
              className='flex h-8 w-full items-center justify-center gap-2 rounded-md px-2 text-sm text-nova-muted-foreground hover:bg-nova-sidebar-accent hover:text-nova-sidebar-accent-foreground'
              aria-pressed={sidebarCollapsed}
              aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <PanelLeft className='h-4 w-4 shrink-0' aria-hidden />
              {sidebarCollapsed ? null : <span className='truncate'>Collapse</span>}
            </button>
            {signOutButton(sidebarCollapsed)}
          </div>
        </aside>
        {mobileMenuOpen ? (
          <div className='fixed inset-0 z-50 lg:hidden'>
            <button
              type='button'
              className='absolute inset-0 bg-black/40'
              aria-label='Close menu'
              onClick={() => setMobileMenuOpen(false)}
            />
            <div className='absolute inset-y-0 left-0 flex w-64 max-w-[85vw] flex-col border-r border-nova-sidebar-border bg-nova-sidebar shadow-lg'>
              <div className='flex h-12 shrink-0 items-center justify-between gap-2 border-b border-nova-sidebar-border px-3'>
                {brand(false)}
                <button
                  type='button'
                  onClick={() => setMobileMenuOpen(false)}
                  className='flex h-8 w-8 items-center justify-center rounded-md text-nova-muted-foreground hover:bg-nova-sidebar-accent'
                  aria-label='Close menu'
                >
                  <X className='h-4 w-4' aria-hidden />
                </button>
              </div>
              <nav
                id='admin-mobile-nav'
                className='flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]'
              >
                {navGroups.map((group) => (
                  <div key={group.id} className='flex flex-col gap-0.5'>
                    <p className='px-2 pt-2 text-[11px] font-medium uppercase tracking-wide text-nova-muted-foreground'>
                      {group.label}
                    </p>
                    {group.items.map((item) =>
                      renderNavLink(item, () => setMobileMenuOpen(false))
                    )}
                  </div>
                ))}
              </nav>
              <div className='border-t border-nova-sidebar-border p-2'>
                {signOutButton(false)}
              </div>
            </div>
          </div>
        ) : null}
        <div className='flex min-w-0 flex-1 flex-col'>
          <header className='sticky top-0 z-40 flex h-12 items-center gap-2 border-b border-nova-border bg-nova-background px-3 lg:hidden'>
            <button
              type='button'
              onClick={() => setMobileMenuOpen(true)}
              className='flex h-8 w-8 items-center justify-center rounded-md text-nova-foreground hover:bg-nova-accent'
              aria-expanded={mobileMenuOpen}
              aria-controls='admin-mobile-nav'
              aria-label='Open menu'
            >
              <Menu className='h-4 w-4' aria-hidden />
            </button>
            {brand(false)}
          </header>
          <main className='min-w-0 flex-1 p-3 sm:p-4'>
            <Outlet />
          </main>
        </div>
      </div>
    </AdminPortalContext.Provider>
  );
}

export const Route = createFileRoute('/admin')({
  component: AdminLayout,
});
